import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClienteCreateEditComponent } from '@nomoh-monorepo/bardo-administrador-feature-estabelecimentos';
import {
  ClienteDataService,
  ClienteService,
  EstabelecimentoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import { BottomSheetComponent, DeviceService } from '@nomoh-monorepo/shared-common-ui';
import { IEstabelecimentoClienteEntity } from '@nomoh-monorepo/shared-types';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import { ConfirmationService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';

interface ModalMensagemState {
  visivel: boolean;
  tipo: 'alerta_telefone' | 'info_dependente';
  titulo: string;
  mensagem: string;
  icon: string;
  cliente?: IEstabelecimentoClienteEntity;
}

@Component({
  selector: 'lib-agenda-lista-cliente',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    BadgeModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    FormsModule,
    SkeletonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    ClienteCreateEditComponent,
    BottomSheetComponent,
    TranslateModule,
  ],
  templateUrl: './agenda-lista-cliente.component.html',
  styleUrl: './agenda-lista-cliente.component.scss',
  providers: [DialogService, ConfirmationService],
})
export class AgendaListaClienteAgendamentoComponent implements OnInit {
  //#region Injeção de Dependências
  deviceService = inject(DeviceService);
  clienteDataService = inject(ClienteDataService);
  $nicho = inject(NichoTermosService).$nicho;
  private clienteService = inject(ClienteService);

  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private confirmationService = inject(ConfirmationService);
  //#endregion
  //#region ViewChild
  @ViewChild(ClienteCreateEditComponent)
  clienteEditRef?: ClienteCreateEditComponent;
  //#endregion
  //#region Outputs
  @Output() cliente = new EventEmitter<IEstabelecimentoClienteEntity>();
  @Output() modoChanged = new EventEmitter<'lista' | 'criar'>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() carregamentoInicial = new EventEmitter<void>();
  //#endregion

  //#region signals
  $isLoading = signal(false);
  $criandoCliente = signal(false);
  $clientes = this.clienteDataService.$clientes;
  $total = this.clienteDataService.$total;
  $loading = this.clienteDataService.$loading;
  private $jaCarregou = signal(false);
  $exibirSkeleton = computed(
    () =>
      !this.$jaCarregou() ||
      (this.$loading() && this.$clientes().length === 0)
  );
  readonly skeletonCards = [1, 2, 3, 4];
  $paginatorDropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );
  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;
  first = signal(0);
  rows = signal(10);
  pesquisa = signal<string | undefined>(undefined);

  $clienteSelecionado = signal<IEstabelecimentoClienteEntity | null>(null);

  $modalMensagem = signal<ModalMensagemState>({
    visivel: false,
    tipo: 'alerta_telefone',
    titulo: '',
    mensagem: '',
    icon: 'pi pi-info-circle',
  });

  $modalDependente = signal<{
    visivel: boolean;
    cliente?: IEstabelecimentoClienteEntity;
  }>({
    visivel: false,
  });
  //#endregion

  private avatarPalette = [
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#F97316',
    '#EA580C',
  ];

  ngOnInit(): void {
    this.carregarClientes();
    this.$criandoCliente.set(false);
  }

  //#region Eventos
  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first.set(event.first ?? 0);
    this.rows.set(event.rows ?? 10);
    this.carregarClientes();
  }

  onPageChange(event: { first: number; rows: number }): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
    this.carregarClientes();
  }

  private searchTimeout?: ReturnType<typeof setTimeout>;

  buscarComFiltro(): void {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.first.set(0);
    this.carregarClientes();
  }

  onPesquisaInput(val: string): void {
    this.pesquisa.set(val);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.first.set(0);
      this.carregarClientes();
    }, 350);
  }

  limparPesquisa(): void {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.pesquisa.set(undefined);
    this.first.set(0);
    this.carregarClientes();
  }

  selectCliente(cliente: IEstabelecimentoClienteEntity, event?: Event): void {
    event?.stopPropagation();

    const possuiTelefoneProprio = !!cliente.telefone && cliente.telefone.trim().length > 0;
    const possuiResponsavel = !!cliente.responsavelId || !!cliente.responsavelNome || !!cliente.responsavelTelefone;

    if (!possuiTelefoneProprio && !possuiResponsavel) {
      const titulo = 'Telefone Necessário para Agendamento';
      const mensagem = `O cliente "${cliente.nome}" não possui telefone próprio cadastrado e não tem um responsável vinculado.\n\nPara realizar agendamentos, acesse o cadastro de clientes e informe um número de telefone para que ele seja o responsável por si mesmo, ou adicione um responsável na aba Vínculos.`;

      if (this.deviceService.$isMobile()) {
        this.$modalMensagem.set({
          visivel: true,
          tipo: 'alerta_telefone',
          titulo,
          mensagem,
          icon: 'pi pi-exclamation-triangle text-amber-500',
          cliente,
        });
      } else {
        this.confirmationService.confirm({
          header: titulo,
          message: mensagem,
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Entendi',
          rejectVisible: false,
        });
      }
      return;
    }

    if (cliente.responsavelNome || cliente.responsavelId) {
      this.exibirInfoResponsavel(cliente, event);
      return;
    }

    // Seleção direta ao clicar no card (Sem necessidade de rolagem)
    this.$clienteSelecionado.set(cliente);
    this.cliente.emit(cliente);
  }

  confirmarClienteSelecionado(cliente?: IEstabelecimentoClienteEntity): void {
    const alvo = cliente || this.$clienteSelecionado();
    if (!alvo) return;
    this.fecharModalDependente();
    this.cliente.emit(alvo);
  }


  iniciarCriacao(): void {
    this.$criandoCliente.set(true);
    this.modoChanged.emit('criar');
  }

  cancelarCriacao(): void {
    this.$criandoCliente.set(false);
    this.modoChanged.emit('lista');
  }

  onFooterTemplate(template: TemplateRef<unknown>): void {
    this.footerTemplateAction.emit(template);
  }

  async salvarCliente(): Promise<void> {
    await this.clienteEditRef?.salvar();
  }

  onClienteSalvo(): void {
    this.cancelarCriacao();
    this.carregarClientes();
  }
  //#endregion

  //#region Carregar tabela
  async carregarClientes() {
    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) {
      this.marcarCarregamentoInicial();
      return;
    }

    try {
      const page = Math.floor(this.first() / this.rows()) + 1;
      await this.clienteService.findAllPaginado(
        estabelecimentoId,
        page,
        this.rows(),
        this.pesquisa()
      );
    } catch {
      // interceptor exibe o erro
    } finally {
      this.marcarCarregamentoInicial();
    }
  }

  private marcarCarregamentoInicial(): void {
    if (this.$jaCarregou()) {
      return;
    }
    this.$jaCarregou.set(true);
    this.carregamentoInicial.emit();
  }
  //#endregion

  formatarTelefoneValor(telefone: string | null | undefined): string {
    if (!telefone) return 'Sem telefone';
    return formatarTelefone(telefone);
  }

  exibirInfoResponsavel(cliente: IEstabelecimentoClienteEntity, event?: Event): void {
    event?.stopPropagation();
    this.$modalDependente.set({
      visivel: true,
      cliente,
    });
  }

  fecharModalDependente(): void {
    this.$modalDependente.set({ visivel: false, cliente: undefined });
  }

  fecharModalMensagem(): void {
    this.$modalMensagem.update((prev) => ({ ...prev, visivel: false }));
  }

  getIniciais(nome: string): string {
    if (!nome) return '';
    return nome
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getAvatarColor(clienteId: string | undefined): string {
    if (!clienteId) return this.avatarPalette[0];
    let sum = 0;
    for (let i = 0; i < clienteId.length; i++) {
      sum += clienteId.charCodeAt(i);
    }
    const index = sum % this.avatarPalette.length;
    return this.avatarPalette[index];
  }
}
